import { useTranslations } from 'next-intl';
import type { TranslateFn } from '@/shared/types/common';
import type {
  ExportFieldKey,
  FileFormatConfig,
  TranscriptExportFormat,
} from '@/shared/types/export';

/**
 * Returns the file format configurations for the export settings
 * with the translations for the file formats and options.
 */
export function useExportFileConfiguration() {
  const t = useTranslations('exports');

  return {
    pdf: createFileFormatConfig(t, 'pdf', PDF_FIELDS),
    txt: createFileFormatConfig(t, 'txt', TXT_FIELDS),
    docx: createFileFormatConfig(t, 'docx', DOCX_FIELDS),
    srt: createFileFormatConfig(t, 'srt', SRT_FIELDS),
  };
}

const PDF_FIELDS: ExportFieldKey[] = [
  'speaker_names',
  'speaker_timestamps',
  'show_highlights',
  'inline_pictures',
  'merge_same_speaker_segments',
];
const TXT_FIELDS: ExportFieldKey[] = [
  'speaker_names',
  'speaker_timestamps',
  'merge_same_speaker_segments',
  'branding',
];
const DOCX_FIELDS: ExportFieldKey[] = [
  'speaker_names',
  'speaker_timestamps',
  'show_highlights',
  'inline_pictures',
  'merge_same_speaker_segments',
];
const SRT_FIELDS: ExportFieldKey[] = [
  'speaker_names',
  'advanced_srt',
  'max_lines',
  'char_per_line',
];

function createFileFormatConfig(
  t: TranslateFn,
  formatKey: TranscriptExportFormat,
  fields: ExportFieldKey[],
): FileFormatConfig {
  const fileName = t(`fileFormats.${formatKey}`);
  const fileExtension = formatKey;
  const label = t('formatSelect.labelFormat', {
    name: fileName,
    extension: fileExtension,
  });
  return {
    formatKey,
    label,
    optionDefinitions: fields.map((field) => ({
      value: field,
      label: t(`options.${field}`),
    })),
  };
}
